import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users as UsersIcon } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";
import DataTable from "@/components/mui/DataTable";

export default function Customers() {
  const onlyDigits = (value = "") => value.replace(/\D/g, "");
  const maskCpfCnpj = (value = "") => {
    const digits = onlyDigits(value).slice(0, 14);
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    return digits
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  };
  const maskCep = (value = "") => {
    const digits = onlyDigits(value).slice(0, 8);
    return digits.replace(/(\d{5})(\d)/, "$1-$2");
  };

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const lastCepRequestedRef = useRef("");
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    cpf_cnpj: "",
    state_registration: "",
    phone: "",
    email: "",
    address: "",
    address_street: "",
    address_number: "",
    address_neighborhood: "",
    address_city_code: "",
    address_city: "",
    address_state: "",
    address_zip_code: "",
  });

  const [pagination, setPagination] = useState({
    page: 1, // Laravel é 1-based
    perPage: 15,
    total: 0,
    lastPage: 0,
  });

  useEffect(() => {
    fetchCustomers(1, pagination.perPage);
  }, []);

  const fillAddressByCep = async (cepDigits) => {
    if (cepDigits.length !== 8 || lastCepRequestedRef.current === cepDigits) {
      return;
    }

    lastCepRequestedRef.current = cepDigits;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const viaCep = response.data;

      if (viaCep?.erro) return;

      setFormData((prev) => ({
        ...prev,
        address_street: viaCep.logradouro || prev.address_street,
        address_neighborhood: viaCep.bairro || prev.address_neighborhood,
        address_city: viaCep.localidade || prev.address_city,
        address_state: (viaCep.uf || prev.address_state || "").toUpperCase().slice(0, 2),
        address_city_code: onlyDigits(viaCep.ibge || prev.address_city_code).slice(0, 7),
      }));
    } catch (_error) {
      // Falha do ViaCEP não deve bloquear o preenchimento manual
    }
  };

  const fetchCustomers = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers`, {
        params: { page, per_page: perPage },
      });

      const data = response.data;

      if (Array.isArray(data)) {
        setCustomers(data);
        setPagination({
          page: 1,
          perPage: data.length,
          total: data.length,
          lastPage: 1,
        });
      } else {
        setCustomers(data.data || []);
        setPagination({
          page: data.current_page,
          perPage: data.per_page,
          total: data.total,
          lastPage: data.last_page,
        });
      }
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        document: onlyDigits(formData.cpf_cnpj),
        state_registration: (formData.state_registration || "").toUpperCase().replace(/[^A-Z0-9]/g, ""),
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        address_street: formData.address_street,
        address_number: formData.address_number,
        address_neighborhood: formData.address_neighborhood,
        address_city_code: onlyDigits(formData.address_city_code),
        address_city: formData.address_city,
        address_state: (formData.address_state || "").toUpperCase(),
        address_zip_code: onlyDigits(formData.address_zip_code),
      };

      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, payload);
        toast.success("Cliente atualizado!");
      } else {
        await axios.post(`${API}/customers`, payload);
        toast.success("Cliente criado!");
      }
      setShowModal(false);
      resetForm();
      fetchCustomers(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar cliente");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este cliente?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success("Cliente excluído!");
      fetchCustomers(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error("Erro ao excluir cliente");
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      cpf_cnpj: maskCpfCnpj(customer.cpf_cnpj || customer.document || ""),
      state_registration: customer.state_registration || "",
      phone: customer.phone || "",
      email: customer.email || "",
      address: customer.address || "",
      address_street: customer.address_street || "",
      address_number: customer.address_number || "",
      address_neighborhood: customer.address_neighborhood || "",
      address_city_code: customer.address_city_code || "",
      address_city: customer.address_city || "",
      address_state: customer.address_state || "",
      address_zip_code: maskCep(customer.address_zip_code || ""),
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      cpf_cnpj: "",
      state_registration: "",
      phone: "",
      email: "",
      address: "",
      address_street: "",
      address_number: "",
      address_neighborhood: "",
      address_city_code: "",
      address_city: "",
      address_state: "",
      address_zip_code: "",
    });
    setEditingCustomer(null);
  };

  const handleChangePage = (event, newPageZeroBased) => {
    const newPage = newPageZeroBased + 1;
    fetchCustomers(newPage, pagination.perPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      perPage: newPerPage,
      page: 1,
    }));
    fetchCustomers(1, newPerPage);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ====== colunas da tabela (DataTable) ======
  const columns = [
    {
      id: "name",
      label: "Nome",
      render: (customer) => (
        <Typography fontWeight={600}>{customer.name}</Typography>
      ),
    },
    {
      id: "document",
      label: "CPF/CNPJ",
      render: (customer) =>
        customer.document || customer.cpf_cnpj || "-",
    },
    {
      id: "phone",
      label: "Telefone",
      render: (customer) => customer.phone || "-",
    },
    {
      id: "email",
      label: "Email",
      render: (customer) => customer.email || "-",
    },
    {
      id: "actions",
      label: "Ações",
      render: (customer) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(customer)}
            data-testid={`edit-customer-${customer.id}`}
          >
            <Pencil size={14} />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(customer.id)}
            data-testid={`delete-customer-${customer.id}`}
          >
            <Trash2 size={14} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const isCnpj = onlyDigits(formData.cpf_cnpj).length === 14;

  const tablePagination = {
    page: pagination.page - 1, // 0-based para o MUI
    rowsPerPage: pagination.perPage,
    total: pagination.total,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
  };

  return (
    <Box data-testid="customers-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Clientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registro de clientes
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          data-testid="add-customer-btn"
        >
          Novo Cliente
        </Button>
      </Box>

      <Paper className="data-table-container">
        {/* Tabela / Empty */}
        {pagination.total === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <UsersIcon size={64} />
            <Typography variant="h6">Nenhum cliente cadastrado</Typography>
            <Typography variant="body2">
              Comece adicionando seu primeiro cliente
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={customers}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `customer-row-${row.id}`}
            pagination={tablePagination}
          />
        )}
      </Paper>

      {/* Dialog criar/editar */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingCustomer ? "Editar Cliente" : "Novo Cliente"}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome"
                fullWidth
                size="small"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                data-testid="customer-name-input"
              />

              <TextField
                label="CPF/CNPJ"
                fullWidth
                size="small"
                value={formData.cpf_cnpj}
                onChange={(e) =>
                  setFormData({ ...formData, cpf_cnpj: maskCpfCnpj(e.target.value) })
                }
                data-testid="customer-cpf-input"
              />

              {isCnpj && (
                <TextField
                  label="Inscrição Estadual (IE)"
                  fullWidth
                  size="small"
                  value={formData.state_registration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      state_registration: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                    })
                  }
                  helperText="Preencha a IE quando o cliente for CNPJ."
                />
              )}

              <TextField
                label="Telefone"
                fullWidth
                size="small"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                data-testid="customer-phone-input"
              />

              <TextField
                label="Email"
                type="email"
                fullWidth
                size="small"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                data-testid="customer-email-input"
              />

              <TextField
                label="Endereço"
                fullWidth
                size="small"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                data-testid="customer-address-input"
              />

              {isCnpj && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Endereço fiscal (NF-e)
                  </Typography>

                  <TextField
                    label="Logradouro"
                    fullWidth
                    size="small"
                    value={formData.address_street}
                    onChange={(e) =>
                      setFormData({ ...formData, address_street: e.target.value })
                    }
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      label="Número"
                      fullWidth
                      size="small"
                      value={formData.address_number}
                      onChange={(e) =>
                        setFormData({ ...formData, address_number: e.target.value })
                      }
                    />
                    <TextField
                      label="Bairro"
                      fullWidth
                      size="small"
                      value={formData.address_neighborhood}
                      onChange={(e) =>
                        setFormData({ ...formData, address_neighborhood: e.target.value })
                      }
                    />
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 2 }}>
                    <TextField
                      label="Cidade"
                      fullWidth
                      size="small"
                      value={formData.address_city}
                      onChange={(e) =>
                        setFormData({ ...formData, address_city: e.target.value })
                      }
                    />
                    <TextField
                      label="UF"
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 2 }}
                      value={formData.address_state}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address_state: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2),
                        })
                      }
                    />
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      label="CEP"
                      fullWidth
                      size="small"
                      value={formData.address_zip_code}
                      onChange={(e) => {
                        const cepDigits = onlyDigits(e.target.value).slice(0, 8);
                        setFormData((prev) => ({
                          ...prev,
                          address_zip_code: maskCep(e.target.value),
                        }));

                        if (cepDigits.length === 8) {
                          void fillAddressByCep(cepDigits);
                        } else {
                          lastCepRequestedRef.current = "";
                        }
                      }}
                    />
                    <TextField
                      label="Cód. Município IBGE"
                      fullWidth
                      size="small"
                      value={formData.address_city_code}
                      onChange={(e) =>
                        setFormData({ ...formData, address_city_code: onlyDigits(e.target.value).slice(0, 7) })
                      }
                    />
                  </Box>
                </>
              )}
            </Box>

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                data-testid="cancel-customer-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-customer-btn"
              >
                {editingCustomer ? "Atualizar" : "Criar"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
